/**
 * Normalizes seller form input without mutating the original object (Sell Your Car M2).
 */
import {
  SELLER_DEFAULT_CURRENCY,
  SELLER_OWNERSHIP_STATUSES,
  SELLER_PREFERRED_CONTACT_METHODS,
  SELLER_TYPES,
} from "./sellerIntakeConstants";

function pickString(value) {
  if (value == null) return "";
  return String(value).trim();
}

function pickNullableString(value) {
  if (value == null) return null;
  const text = String(value).trim();
  return text || null;
}

function pickPositiveInt(value) {
  if (value == null || value === "") return null;
  const num = Number(value);
  if (!Number.isFinite(num) || num < 0) return null;
  return Math.round(num);
}

function pickPositiveNumber(value) {
  if (value == null || value === "") return null;
  const num = Number(value);
  if (!Number.isFinite(num) || num < 0) return null;
  return num;
}

function pickEnum(value, allowed, fallback) {
  const normalized = pickString(value);
  return allowed.includes(normalized) ? normalized : fallback;
}

const SELLER_TYPE_VALUES = Object.values(SELLER_TYPES);
const CONTACT_METHOD_VALUES = Object.values(SELLER_PREFERRED_CONTACT_METHODS);
const OWNERSHIP_VALUES = Object.values(SELLER_OWNERSHIP_STATUSES);

/**
 * @param {import('./sellerIntakeTypes').SellerSubmissionInput|object} [input]
 * @returns {import('./sellerIntakeTypes').SellerSubmissionInput}
 */
export function normalizeSellerSubmissionInput(input = {}) {
  return {
    sellerType: pickEnum(input.sellerType, SELLER_TYPE_VALUES, SELLER_TYPES.INDIVIDUAL),
    sellerName: pickString(input.sellerName),
    sellerPhone: pickString(input.sellerPhone),
    sellerEmail: pickString(input.sellerEmail),
    preferredContactMethod: pickEnum(
      input.preferredContactMethod,
      CONTACT_METHOD_VALUES,
      SELLER_PREFERRED_CONTACT_METHODS.WHATSAPP
    ),
    city: pickString(input.city),
    department: pickString(input.department),
    vehicleMake: pickString(input.vehicleMake),
    vehicleModel: pickString(input.vehicleModel),
    vehicleYear: pickPositiveInt(input.vehicleYear),
    vehicleTrim: pickString(input.vehicleTrim),
    vehicleMileageKm: pickPositiveInt(input.vehicleMileageKm),
    askingPrice: pickPositiveNumber(input.askingPrice),
    currency: pickString(input.currency) || SELLER_DEFAULT_CURRENCY,
    conditionSummary: pickString(input.conditionSummary),
    ownershipStatus: pickEnum(
      input.ownershipStatus,
      OWNERSHIP_VALUES,
      SELLER_OWNERSHIP_STATUSES.UNKNOWN
    ),
    plate: pickString(input.plate),
    vin: pickString(input.vin),
    hasPhotos: Boolean(input.hasPhotos),
    notes: pickString(input.notes),
    consentAccepted: Boolean(input.consentAccepted),
    marketingOptIn: Boolean(input.marketingOptIn),
    honeypot: pickString(input.honeypot),
  };
}

/**
 * Maps normalized input to flat record field names (for storage documentation / admin previews).
 * Does not assign id/timestamps — server responsibility in later milestones.
 * @param {import('./sellerIntakeTypes').SellerSubmissionInput} input
 * @param {import('./sellerIntakeTypes').SellerAttributionContext} [attribution]
 * @param {{ clientSubmittedAt?: string, privacyPolicyVersion?: string, photoCount?: number }} [options]
 * @returns {Partial<import('./sellerIntakeTypes').SellerSubmissionRecord>}
 */
export function mapSellerInputToRecordFields(input, attribution = {}, options = {}) {
  const submittedAt = options.clientSubmittedAt || new Date().toISOString();
  const photoCount = options.photoCount ?? (input.hasPhotos ? 0 : 0);

  return {
    sellerType: input.sellerType,
    sellerName: input.sellerName || null,
    sellerPhone: input.sellerPhone || null,
    sellerEmail: pickNullableString(input.sellerEmail),
    preferredContactMethod: input.preferredContactMethod,
    city: input.city || null,
    department: pickNullableString(input.department),
    vehicleMake: input.vehicleMake || null,
    vehicleModel: input.vehicleModel || null,
    vehicleYear: input.vehicleYear,
    vehicleTrim: pickNullableString(input.vehicleTrim),
    vehicleMileageKm: input.vehicleMileageKm,
    askingPrice: input.askingPrice,
    currency: input.currency,
    conditionSummary: pickNullableString(input.conditionSummary),
    ownershipStatus: input.ownershipStatus,
    plate: pickNullableString(input.plate),
    vin: pickNullableString(input.vin),
    hasPhotos: input.hasPhotos,
    photoCount,
    notes: pickNullableString(input.notes),
    consentAccepted: input.consentAccepted,
    consentAcceptedAt: input.consentAccepted ? submittedAt : null,
    privacyPolicyVersion: options.privacyPolicyVersion || "automap-privacy-v1",
    marketingOptIn: input.marketingOptIn,
    sourcePage: attribution.sourcePage || null,
    sourceComponent: attribution.sourceComponent || null,
    sourceAction: attribution.sourceAction || null,
    currentPath: attribution.currentPath || null,
    referrer: attribution.referrer || null,
    utmSource: attribution.utmSource || null,
    utmMedium: attribution.utmMedium || null,
    utmCampaign: attribution.utmCampaign || null,
    utmContent: attribution.utmContent || null,
    utmTerm: attribution.utmTerm || null,
  };
}
